import { BUILD_ID } from '../buildInfo';
import { getApiBase } from './serverConfig';

/*
 * Telling the server about a crash in the browser.
 *
 * ────────────────────────────────────────────────────────────────────────
 * WHY NOT utils/api.js
 * ────────────────────────────────────────────────────────────────────────
 *
 * The shared axios client has two interceptors that are exactly wrong here.
 * On a 401 it clears the token and sets window.location to /login, and on a
 * 426 it may reload -- so a crash report that happened to fail would navigate
 * away from the error screen and destroy the evidence. It also attaches the
 * bearer token, which this endpoint does not want and does not read.
 *
 * sendBeacon is the right shape besides: fire-and-forget, queued by the
 * browser, and it survives the page going away -- which a crashed app often
 * does, because the first thing people do with a blank screen is reload it.
 *
 * ────────────────────────────────────────────────────────────────────────
 * IT CAN NEVER THROW
 * ────────────────────────────────────────────────────────────────────────
 *
 * This runs from componentDidCatch. An exception here would be thrown while
 * React is handling an exception, and the fallback UI would not render at
 * all -- turning a caught crash into a blank page, which is the exact thing
 * the error boundary exists to prevent. So everything is wrapped, and the
 * function returns whether the report went out rather than raising.
 */

//: The endpoint takes the path, not the href. window.location.href carries
//: the query string, and a query string in this app carries record ids.
function currentPath() {
  try {
    return window.location.pathname || '/';
  } catch {
    return '/';
  }
}

export function buildReport(error, info) {
  return {
    message: String(error?.message || error || 'Unknown error'),
    stack: String(error?.stack || ''),
    component_stack: String(info?.componentStack || ''),
    path: currentPath(),
    app_version: String(BUILD_ID || ''),
    user_agent: String(navigator?.userAgent || ''),
  };
}

export function reportClientError(error, info) {
  try {
    const url = `${getApiBase() || ''}/telemetry/client-error`;
    const body = JSON.stringify(buildReport(error, info));

    if (navigator?.sendBeacon) {
      // A Blob with a type, or the browser sends text/plain and the API
      // parses nothing.
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(url, blob)) return true;
      // sendBeacon returns false when the payload is over the browser's
      // queue limit; fall through rather than losing the report.
    }

    if (typeof fetch === 'function') {
      // keepalive, for the same reason as sendBeacon: the reload usually
      // wins the race otherwise.
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true,
      }).catch(() => {});
      return true;
    }
  } catch {
    // Reporting is best effort. A browser that blocks it still gets the
    // fallback screen, which is the part that matters to the person.
  }
  return false;
}
